import { PersonInfoApi, NameType } from "../../api/Person";
import { PersonInfo } from "../../services/extension/webSearchPort";

/**
 * Transform and validate personEx to PersonInfo
 * Returns null if personEx is invalid or missing required fields
 */

export function transformPersonInfo(
  personEx?: PersonInfoApi
): PersonInfo | null {
  if (!personEx) return null;

  try {
    // Validate required fields exist
    if (!personEx.names || personEx.names.length === 0) {
      console.warn(
        "[WebResultsView] No names in personEx, skipping personinfo"
      );
      return null;
    }

    // Separate primary name (MAIN) from aliases (ALIAS)
    // Filter out empty MAIN names first
    const validMainNames = personEx.names
      .filter((name) => name.name_type === NameType.MAIN)
      .filter((name) => name.first_name?.trim() || name.last_name?.trim());

    const primaryNameObj = validMainNames.length > 0 ? validMainNames[0] : null;

    // Build alias names array (will be filtered later if one is used as primary)
    const aliasNames = personEx.names
      .filter((name) => name.name_type === NameType.ALIAS)
      .map((name) => ({
        first_name: name.first_name?.trim() || "",
        middle_name: name.middle_name?.trim(),
        last_name: name.last_name?.trim() || "",
      }))
      .filter((name) => name.first_name || name.last_name); // At least one name part

    // If no MAIN name found, use first valid name as primary (fallback)
    let primaryName: {
      first_name: string;
      middle_name?: string;
      last_name: string;
    };
    let selectedAliasIndex: number | null = null;
    if (primaryNameObj) {
      primaryName = {
        first_name: primaryNameObj.first_name?.trim() || "",
        middle_name: primaryNameObj.middle_name?.trim(),
        last_name: primaryNameObj.last_name?.trim() || "",
      };
    } else {
      // Fallback: prefer non-ALIAS names first, then ALIAS names if no other option
      // First try to find a valid non-ALIAS name
      const firstValidNonAliasName = personEx.names.find(
        (name) =>
          name.name_type !== NameType.ALIAS &&
          (name.first_name?.trim() || name.last_name?.trim())
      );

      if (firstValidNonAliasName) {
        primaryName = {
          first_name: firstValidNonAliasName.first_name?.trim() || "",
          middle_name: firstValidNonAliasName.middle_name?.trim(),
          last_name: firstValidNonAliasName.last_name?.trim() || "",
        };
      } else {
        // No non-ALIAS names found, use first ALIAS name as primary
        // Find which alias index was selected so we can remove it from aliases array
        const firstValidAliasName = personEx.names.find(
          (name) =>
            name.name_type === NameType.ALIAS &&
            (name.first_name?.trim() || name.last_name?.trim())
        );
        if (!firstValidAliasName) {
          console.warn("[WebResultsView] No valid names after filtering");
          return null;
        }
        primaryName = {
          first_name: firstValidAliasName.first_name?.trim() || "",
          middle_name: firstValidAliasName.middle_name?.trim(),
          last_name: firstValidAliasName.last_name?.trim() || "",
        };
        // Find the index of this alias in the aliasNames array to remove it
        selectedAliasIndex = aliasNames.findIndex(
          (alias) =>
            alias.first_name === primaryName.first_name &&
            alias.last_name === primaryName.last_name &&
            alias.middle_name === primaryName.middle_name
        );
      }
    }

    // Validate primary name has at least one part (should never happen after filtering, but double-check)
    if (!primaryName.first_name && !primaryName.last_name) {
      console.warn("[WebResultsView] Primary name has no valid parts");
      return null;
    }

    // Extract locations (deduplicate, filter empty)
    const locations = new Set<string>();
    personEx.addresses?.forEach((addr) => {
      if (addr.city?.trim()) locations.add(addr.city.trim());
    });
    personEx.jobs?.forEach((job) => {
      if (job.country?.trim()) locations.add(job.country.trim());
    });
    personEx.degrees?.forEach((degree) => {
      if (degree.country?.trim()) locations.add(degree.country.trim());
      if (degree.educator?.trim()) locations.add(degree.educator.trim());
    });

    // Remove the selected alias from aliases array if it was used as primary
    const finalAliases =
      selectedAliasIndex !== null && selectedAliasIndex >= 0
        ? aliasNames.filter((_, index) => index !== selectedAliasIndex)
        : aliasNames;

    return {
      primary_name: primaryName,
      aliases: finalAliases.length > 0 ? finalAliases : undefined,
      birth_date: personEx.birth_date?.trim() || undefined,
      locations: Array.from(locations).filter(Boolean),
    };
  } catch (error) {
    console.error("[WebResultsView] Error transforming personinfo:", error);
    return null; // Fail gracefully - don't send malformed data
  }
}
